import { FetchPolicy, useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { IItinerary } from 'common/types';
import ADD_HISTORIES from './historyAdd.gql';
import DELETE_HISTORY from './historyDelete.gql';
import SEARCH_HISTORIES from './historySearch.gql';

interface ISearchHistory {
  historyId: string;
  timestamp: number;
}

interface ITrip {
  departureDate: string;
  departureCityCode: string;
  destinationCityCode: string;
}

class Trip implements ITrip {
  departureDate: string;

  departureCityCode: string;

  destinationCityCode: string;

  constructor(itinerary: IItinerary) {
    const { departureDate, departureAirport, arrivalAirport } = itinerary;
    this.departureDate = departureDate;
    this.departureCityCode = departureAirport;
    this.destinationCityCode = arrivalAirport;
  }
}

export interface ITripDesc extends ITrip {
  departureCityName: string;
  isDomesticDeparture: boolean;
  destinationCityName: string;
  isDomesticDestination: boolean;
}

export interface IPassenger {
  adult: number;
  child: number;
  infant: number;
}

export interface IFlightSearchHistory extends ISearchHistory {
  trip: string;
  fareType: string;
  searchType: string;
  isDirect: boolean;
  passenger: IPassenger;
  tripList: ITripDesc[];
}

export interface IFlightHistoryInput {
  trip: string;
  fareType: string;
  searchType: string;
  isDirect: boolean;
  passenger: IPassenger;
  tripList: ITrip[];
}

interface IData {
  searchHistories: IFlightSearchHistory[];
}

const useSearchHistories = (count?: number, fetchPolicy?: FetchPolicy) =>
  useQuery<IData>(SEARCH_HISTORIES, {
    variables: { count },
    ssr: false,
    fetchPolicy: fetchPolicy || 'cache-and-network',
  });

const useAddHistory = (history: IFlightHistoryInput) => {
  const [addHistory] = useMutation(ADD_HISTORIES);

  useEffect(() => {
    addHistory({ variables: { history } });
  }, []);
};

const useDeleteHistory = (count?: number) => {
  const [deleteHistory] = useMutation(DELETE_HISTORY, {
    refetchQueries: [{ query: SEARCH_HISTORIES, variables: { count } }],
  });

  return (historyId: string) =>
    deleteHistory({ variables: { hashKey: historyId } });
};

export { useSearchHistories, useAddHistory, useDeleteHistory, Trip };
